import { Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { ProxyOptions } from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-constants.ts";
import { classNames } from "@via/utils/helpers.tsx";
import {
  createRunEnvironmentOptions,
  requiredValidation,
} from "@via/utils/form-validators.tsx";
import {
  type ProfileClusterForClient,
  type RunEnvironmentTransport,
} from "common";
import { type JSX, useState } from "react";
import {
  type FieldErrors,
  type UseFormGetValues,
  type UseFormRegister,
} from "react-hook-form";

export const connectionCardSummary = (params: {
  username?: string | null;
  hostname?: string | null;
  port?: string | null;
  sshKeyLabel?: string;
}): string => {
  const host = params.hostname?.trim();
  if (!host) return "No host";
  const user = params.username?.trim();
  const port = params.port?.trim();
  const target = `${user ? `${user}@` : ""}${host}${port ? `:${port}` : ""}`;
  return params.sshKeyLabel ? `${target} (${params.sshKeyLabel})` : target;
};

// "Username is required" plus the shared check that rejects a value of only
// spaces, which is the same pair the Name field gets. Built once at module
// scope rather than on every render.
const usernameRules = requiredValidation("Username");

// The shared validator takes a string, while the payload types username as
// string | null, so the value is normalized before it is handed over.
const usernameIsNotOnlySpaces = (value: string | null): string | true =>
  usernameRules.validate?.(value ?? "") ?? true;

export function ConnectionCard({
  register,
  errors,
  sshPickerShown,
  selectedProxy,
  onProxyChange,
  sshCredentials,
  isSavedCredentialsLoading,
  selectedSshId,
  onSshIdChange,
  getValues,
  testSSHConnection,
  isTestSSHConnectionLoading,
  watchedUsername,
  watchedHostname,
  hasHost,
  transport,
  sshIdError,
}: {
  register: UseFormRegister<ProfileClusterForClient>;
  errors: FieldErrors<ProfileClusterForClient>;
  // Whether the ssh picker is rendered at all. The parent derives this once
  // and uses the same boolean for the submit gate, so the two cannot diverge.
  sshPickerShown: boolean;
  selectedProxy: string;
  onProxyChange: (option: any) => void;
  // The saved-credentials API types name as optional, so the label is too.
  sshCredentials: { label: string | undefined; value: number }[];
  isSavedCredentialsLoading: boolean;
  selectedSshId?: number;
  onSshIdChange: (option: any) => void;
  getValues: UseFormGetValues<ProfileClusterForClient>;
  testSSHConnection: (
    payload: unknown,
    options?: {
      onSuccess?: (response: { success: boolean; error?: string }) => void;
      onError?: () => void;
    }
  ) => void;
  isTestSSHConnectionLoading: boolean;
  watchedUsername?: string | null;
  watchedHostname?: string | null;
  // A hostname being set is what turns the credential fields into required
  // ones. The host itself is the trigger, so it carries no rule of its own.
  hasHost: boolean;
  // The transport chosen in the Basics card. An agent environment has no
  // host and no key by design, so the credential rules a hostname would
  // otherwise turn on must stay off for it too.
  transport?: RunEnvironmentTransport;
  // sshId is component state rather than a registered field, so its error is
  // raised by the submit handler and passed down.
  sshIdError: string | null;
}): JSX.Element {
  const [lastResult, setLastResult] = useState<{
    ok: boolean;
    error?: string;
    at: Date;
  } | null>(null);

  // An agent environment has no key and no host by design, so the credential
  // rules that a hostname turns on must stay off for it. Design 5.2.
  // The "agent" !== transport half is unreachable today: the early return
  // just below already leaves this component before sshRequired is read for
  // an agent environment. Kept as defense in depth, so a future refactor
  // that removes that early return does not silently inherit a required ssh
  // key along with it.
  const sshRequired = hasHost && "agent" !== transport;

  if ("agent" === transport) {
    return (
      <p className="text-sm text-gray-500">
        This environment is reached by your agent, so it needs no SSH key and
        none is stored. Save the environment, then attach your agent below.
      </p>
    );
  }

  return (
    <>
      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Username{" "}
          {sshRequired ? <span className="text-red-400">*</span> : null}
        </label>
        <div className="mt-2">
          <input
            // requiredValidation bundles the message with the shared "cannot
            // be just spaces" check, which the Name field already uses. Both
            // keys are always passed, false and undefined when there is no
            // host, because react-hook-form merges register options onto the
            // previous ones: an omitted key would leave the old rule attached
            // after the host is cleared.
            {...register("username", {
              required: sshRequired ? usernameRules.required : false,
              validate: sshRequired ? usernameIsNotOnlySpaces : undefined,
            })}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="e.g. jdoe"
          />
          {/*
            Gated on the live condition, the same way the ssh message is.
            react-hook-form keeps an error until the field is revalidated, so
            clearing the host after a blocked save leaves "Username is
            required" on screen for a rule that is no longer in force.
          */}
          {sshRequired && errors?.username ? (
            <span className="text-xs text-rose-500">
              {errors.username.message}
            </span>
          ) : null}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Host
        </label>
        <div className="mt-2">
          <input
            {...register("hostname")}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="e.g. hpc.example.edu"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          SSH Port (optional)
        </label>
        <div className="mt-2">
          <input
            {...register("port")}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="Your SSH Port"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Proxy (optional)
        </label>
        <div className="mt-2" data-testid="proxy">
          <SelectComponent
            rules={createRunEnvironmentOptions.proxy}
            onChange={onProxyChange}
            value={ProxyOptions.find(({ value }) => value === selectedProxy)}
            name="proxy"
            options={ProxyOptions}
            enableClear={false}
          />
          {errors.proxy && (
            <span className="text-xs text-rose-500">
              {errors.proxy.message}
            </span>
          )}
        </div>
      </div>
      {sshPickerShown && (
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Your SSH key{" "}
            {sshRequired ? <span className="text-red-400">*</span> : null}
          </label>
          <p className="text-xs text-gray-500">
            Runs on this environment authenticate with this key. Only your own
            keys are listed.
          </p>
          <div className="mt-2" data-testid="ssh-id">
            <SelectComponent
              name="sshId"
              onChange={onSshIdChange}
              value={sshCredentials.find(ssh => ssh.value === selectedSshId)}
              options={sshCredentials}
              isLoading={isSavedCredentialsLoading}
              enableClear={false}
              placeholder={"Select SSH Keys"}
            />
            {sshIdError ? (
              <span className="text-xs text-rose-500">{sshIdError}</span>
            ) : null}
          </div>
        </div>
      )}

      <div>
        <button
          type="button"
          data-testid="test-connection"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          disabled={
            isTestSSHConnectionLoading ||
            !selectedSshId ||
            !watchedUsername ||
            !watchedHostname
          }
          onClick={() => {
            const port = getValues("port");
            testSSHConnection(
              {
                sshId: selectedSshId,
                username: getValues("username"),
                hostname: getValues("hostname"),
                ...(port && { port }), // Add port only if it's not empty
              },
              {
                // The hook already raises a global notification. These add the
                // inline chip beside the fields the test actually used.
                onSuccess: (response: { success: boolean; error?: string }) => {
                  setLastResult({
                    ok: response.success,
                    error: response.error,
                    at: new Date(),
                  });
                },
                onError: () => {
                  setLastResult({
                    ok: false,
                    error: "The request did not reach the server.",
                    at: new Date(),
                  });
                },
              }
            );
          }}>
          <Spinner loading={isTestSSHConnectionLoading}>
            Test Connection
          </Spinner>
        </button>

        {lastResult ? (
          <p
            data-testid="test-connection-result"
            className={classNames(
              "mt-2 text-xs",
              lastResult.ok ? "text-emerald-600" : "text-rose-500"
            )}>
            {lastResult.ok
              ? "Connected"
              : `Failed: ${lastResult.error ?? "unknown error"}`}
            {" · checked "}
            {lastResult.at.toLocaleTimeString()}
          </p>
        ) : null}
      </div>
    </>
  );
}
